/**
 * File Validation Configuration
 *
 * Centralized configuration for file upload validation rules.
 * Defines allowed/blocked extensions, MIME types, and security policies.
 */

export interface FileValidationConfig {
  allowedExtensions: string[];
  blockedExtensions: string[];
  allowedMimeTypes: Record<string, string[]>;
  magicNumbers: Record<string, number[][]>;
  enableMagicNumberValidation: boolean;
}

/**
 * Default file validation configuration
 *
 * Security approach: Defense in depth with explicit allow/block lists
 */
export const DEFAULT_FILE_VALIDATION_CONFIG: FileValidationConfig = {
  /**
   * Allowed file extensions (whitelist)
   * Only these file types can be uploaded
   */
  allowedExtensions: [
    // Documents
    ".pdf",
    ".doc",
    ".docx",
    ".pptx",

    // Images
    ".png",
    ".jpg",
    ".jpeg",
    ".svg",

    // Spreadsheets
    ".csv",
    ".xlsx",
    ".xls",

    // Text files
    ".txt",
    ".md",

    // Configuration/Data
    ".json",
    ".yaml",
    ".yml",
    ".xml",
  ],

  /**
   * Blocked file extensions (blacklist)
   * These file types are explicitly forbidden for security
   */
  blockedExtensions: [
    // Executables
    ".exe",
    ".dll",
    ".so",
    ".dylib",
    ".app",
    ".deb",
    ".rpm",

    // Scripts
    ".js",
    ".mjs",
    ".cjs", // JavaScript
    ".py",
    ".pyc",
    ".pyo", // Python
    ".sh",
    ".bash",
    ".zsh",
    ".fish", // Shell scripts
    ".bat",
    ".cmd",
    ".ps1", // Windows scripts
    ".vbs",
    ".vbe",
    ".wsf",
    ".wsh", // VBScript
    ".rb",
    ".pl",
    ".php", // Other scripts

    // Archives (can contain malicious files)
    ".zip",
    ".rar",
    ".7z",
    ".tar",
    ".gz",
    ".bz2",
    ".xz",
    ".iso",
    ".dmg",
    ".pkg",

    // Macro-enabled Office files
    ".xlsm",
    ".xlsb",
    ".xltm",
    ".xltx", // Excel macros
    ".docm",
    ".dotm", // Word macros
    ".pptm",
    ".potm",
    ".ppsm", // PowerPoint macros

    // Other high-risk
    ".jar",
    ".war",
    ".apk", // Java/Android
    ".com",
    ".scr",
    ".pif", // Legacy Windows
    ".lnk",
    ".url", // Shortcuts (can execute)
    ".htm",
    ".html",
    ".mht",
    ".mhtml", // HTML (XSS risk)
    ".hta", // HTML applications
    ".reg", // Registry files
    ".crt",
    ".pem",
    ".key",
    ".p12", // Certificates/keys
  ],

  /**
   * Expected MIME types for each allowed extension
   * Used to detect spoofed file extensions
   */
  allowedMimeTypes: {
    // Documents
    ".pdf": ["application/pdf"],
    ".doc": ["application/msword"],
    ".docx": ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
    ".pptx": ["application/vnd.openxmlformats-officedocument.presentationml.presentation"],

    // Images
    ".png": ["image/png"],
    ".jpg": ["image/jpeg"],
    ".jpeg": ["image/jpeg"],
    ".svg": ["image/svg+xml"],

    // Spreadsheets
    ".csv": ["text/csv", "application/vnd.ms-excel"],
    ".xlsx": ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    ".xls": ["application/vnd.ms-excel"],

    // Text files
    ".txt": ["text/plain"],
    ".md": ["text/markdown", "text/plain"],

    // Configuration/Data
    ".json": ["application/json"],
    ".yaml": ["text/yaml", "application/x-yaml", "text/x-yaml"],
    ".yml": ["text/yaml", "application/x-yaml", "text/x-yaml"],
    ".xml": ["application/xml", "text/xml"],
  },

  /**
   * Magic numbers (file signatures) for content validation
   * First bytes that identify true file type regardless of extension
   */
  magicNumbers: {
    ".pdf": [[0x25, 0x50, 0x44, 0x46]], // %PDF
    ".png": [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]], // PNG signature
    ".jpg": [[0xff, 0xd8, 0xff]], // JPEG
    ".jpeg": [[0xff, 0xd8, 0xff]], // JPEG

    // Office Open XML formats (ZIP-based)
    ".docx": [[0x50, 0x4b, 0x03, 0x04]], // ZIP signature
    ".xlsx": [[0x50, 0x4b, 0x03, 0x04]], // ZIP signature
    ".pptx": [[0x50, 0x4b, 0x03, 0x04]], // ZIP signature

    // Note: Text files (.txt, .md, .csv, .json, .yaml, .xml) don't have
    // reliable magic numbers as they can start with any text content
  },

  /**
   * Enable magic number validation
   * Can be disabled if causing performance issues
   */
  enableMagicNumberValidation: true,
};

// Export individual lists for convenience
export const { allowedExtensions, blockedExtensions } = DEFAULT_FILE_VALIDATION_CONFIG;
